<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">
            <v-row align="center" justify="start" no-gutters>
              <router-link :to="{ name: 'AgentList' }" class="subtitle-1 routerLink font-weight-medium">Agentes</router-link>
              <v-icon class="mx-1" color="secondary" small>mdi-chevron-right</v-icon>
              <span class="subtitle-1 font-weight-medium">Nuevo agente</span>
            </v-row>
          </v-toolbar-title>
        </v-toolbar>
        <v-divider />

        <v-stepper v-model="step" flat>
          <v-stepper-header class="elevation-0">
            <v-stepper-step :complete="step > 1" step="1">Plantilla</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="step > 2" step="2">Tareas</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="step > 3" step="3">Autonomía</v-stepper-step>
            <v-divider />
            <v-stepper-step step="4">Nombre</v-stepper-step>
          </v-stepper-header>

          <v-divider />

          <v-card-text class="pa-0 background overflow-y-auto" style="height: calc(100vh - 312px)">
            <v-stepper-items>

            <!-- step 1: template -->
            <v-stepper-content step="1">
              <div class="px-3">
                <span class="d-block mb-1 subtitle-2 text--secondary font-weight-medium">Elige una plantilla</span>
                <span class="d-block mb-5 body-2 text--primary">Comienza con un conjunto de tareas predefinido o configura un agente desde cero.</span>
              </div>
              <v-row class="pa-3">
                <v-col cols="12" sm="4">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 d-flex flex-column cursor-pointer" :class="{ 'v-sheet--active': selectedKind === 'collections' }" @click="selectTemplate('collections')" outlined rounded="lg" height="100%" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar class="mr-4" rounded="circle" size="62">
                        <img :src="require('@/assets/appAsset-agents-avatar-1.jpg')" />
                      </v-avatar>
                      <div class="my-3 subtitle-2 text--secondary font-weight-medium">Analista de recaudación</div>
                      <div class="body-2 text--primary flex-grow-1 mb-4">
                        Automatiza la gestión de cobranza: reintentos de pago, contacto con clientes, priorización y escalaciones.
                      </div>
                      <div class="caption text--disabled mb-3">10 tareas disponibles</div>
                      <v-btn outlined block @click.stop="selectTemplate('collections')">Usar plantilla</v-btn>
                    </v-sheet>
                  </v-hover>
                </v-col>

                <v-col cols="12" sm="4">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 d-flex flex-column cursor-pointer" :class="{ 'v-sheet--active': selectedKind === 'billing' }" @click="selectTemplate('billing')" outlined rounded="lg" height="100%" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar class="mr-4" rounded="circle" size="62">
                        <img :src="require('@/assets/appAsset-agents-avatar-6.jpg')" />
                      </v-avatar>
                      <div class="my-3 subtitle-2 text--secondary font-weight-medium">Asistente de facturación</div>
                      <div class="body-2 text--primary flex-grow-1 mb-4">
                        Monitorea y automatiza el ciclo de facturación: verificaciones previas, solicitud de OCs y alertas de bloqueo.
                      </div>
                      <div class="caption text--disabled mb-3">6 tareas disponibles</div>
                      <v-btn outlined block @click.stop="selectTemplate('billing')">Usar plantilla</v-btn>
                    </v-sheet>
                  </v-hover>
                </v-col>

                <v-col cols="12" sm="4">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 d-flex flex-column cursor-pointer" :class="{ 'v-sheet--active': selectedKind === 'scratch' }" @click="selectTemplate('scratch')" outlined rounded="lg" height="100%" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar class="mr-4" rounded="circle" size="62">
                        <img :src="require('@/assets/appAsset-agents-avatar-9.jpg')" />
                      </v-avatar>
                      <div class="my-3 subtitle-2 text--secondary font-weight-medium">Desde cero</div>
                      <div class="body-2 text--primary flex-grow-1 mb-4">
                        Elige libremente las tareas que quieres activar, además de su nivel de autonomía, sin estar limitado a un tipo de agente.
                      </div>
                      <div class="caption text--disabled mb-3">16 tareas disponibles</div>
                      <v-btn outlined block @click.stop="selectTemplate('scratch')">Crear desde cero</v-btn>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>
            </v-stepper-content>
            <!-- end step 1: template -->

            <!-- step 2: tasks -->
            <v-stepper-content step="2" class="pa-6">

              <div class="mb-1 subtitle-2 text--secondary font-weight-medium">Selecciona las tareas a activar</div>
              <div class="body-2 text--primary mb-5">
                {{ enabledTasksCount }} de {{ availableTasks.length }} tareas activadas
              </div>

              <v-card v-for="task in availableTasks" :key="task.task_key" outlined class="mb-3">
                <v-list-item class="py-1">
                  <v-list-item-action class="mr-2">
                    <v-switch v-model="task.enabled" inset color="primary" hide-details dense />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="subtitle-2 text--secondary font-weight-medium">{{ task.title }}</v-list-item-title>
                    <v-list-item-subtitle class="body-2 text--primary">{{ task.description }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-card>

            </v-stepper-content>
            <!-- end step 2: tasks -->

            <!-- step 3: autonomy -->
            <v-stepper-content step="3" class="pa-6">

              <div class="mb-1 subtitle-2 text--secondary font-weight-medium">¿Cómo debe actuar el agente?</div>
              <div class="mb-6 body-2 text--primary">
                Define el nivel de autonomía por defecto para todas las tareas.
              </div>

              <v-row class="px-1">
                <v-col cols="12" sm="4" v-for="option in autonomyOptions" :key="option.value">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-5 text-center cursor-pointer d-flex flex-column align-center" :class="{ 'v-sheet--active': form.default_autonomy === option.value }" @click="form.default_autonomy = option.value" outlined rounded="lg" height="100%" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary">{{ option.icon }}</v-icon></v-avatar>
                      <span class="d-block mt-3 mb-2 body-2 text--secondary font-weight-medium">{{ option.label }}</span>
                      <span class="d-block mb-3 body-2 text--primary flex-grow-1">{{ option.description }}</span>
                      <v-chip v-if="option.recommended" small color="primary" text-color="white">Recomendado</v-chip>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>

            </v-stepper-content>
            <!-- end step 3: autonomy -->

            <!-- step 4: name & avatar -->
            <v-stepper-content step="4" class="pa-6">

              <div class="mb-1 subtitle-2 text--secondary font-weight-medium">Nombre y avatar</div>
              <div class="body-2 text--primary mb-6">
                Dale una personalidad a tu agente. Puedes llamarlo Max, Valentina, Andres...
              </div>

              <!-- avatar picker -->
              <div class="mb-3 subtitle-2 text--secondary font-weight-medium">Elige un avatar</div>
              <v-row dense class="mb-6">
                <v-col cols="auto" v-for="av in avatarOptions" :key="av.value">
                  <v-avatar class="cursor-pointer" @click="form.avatar = av.value" :class="{ 'avatar-selected': form.avatar === av.value, 'outlined': form.avatar !== av.value }" size="48">
                    <img :alt="av.label" :src="require(`@/assets/appAsset-agents-${av.value}.jpg`)" />
                  </v-avatar>
                </v-col>
              </v-row>

              <!-- name -->
              <v-row align="center" class="mb-2">
                <v-col>
                  <v-text-field
                    v-model="form.name"
                    label="Nombre del agente"
                    hint="Como si fuera una persona del equipo: Max, Valentina, Andres..."
                    persistent-hint
                    outlined
                    dense
                    :counter="64"
                    maxlength="64"
                  />
                </v-col>
                <v-col cols="auto" class="pb-5">
                  <v-btn outlined color="secondary" small @click="suggestName">
                    <v-icon left small>mdi-auto-fix</v-icon>Sugerir
                  </v-btn>
                </v-col>
              </v-row>

              <!-- description -->
              <v-row align="start" class="mt-1">
                <v-col>
                  <v-textarea
                    v-model="form.description"
                    label="Descripción (opcional)"
                    outlined
                    dense
                    hide-details
                    rows="3"
                    :counter="256"
                    maxlength="256"
                    no-resize
                  />
                </v-col>
                <v-col cols="auto" class="pt-4">
                  <v-btn outlined color="secondary" small @click="suggestDescription">
                    <v-icon left small>mdi-auto-fix</v-icon>Sugerir
                  </v-btn>
                </v-col>
              </v-row>

            </v-stepper-content>
            <!-- ─── END STEP 4 ──────────────────────────────────────────────── -->

            </v-stepper-items>
          </v-card-text>
        </v-stepper>

        <v-divider />
        <v-card-actions class="pa-4">
          <template v-if="step === 1">
            <v-btn outlined :to="{ name: 'AgentList' }">Cancelar</v-btn>
          </template>
          <template v-if="step === 2">
            <v-btn @click="step = 1" outlined><v-icon left small>mdi-chevron-left</v-icon>Anterior</v-btn>
            <v-spacer />
            <v-btn @click="step = 3" color="primary" :disabled="enabledTasksCount === 0">Siguiente <v-icon right small>mdi-chevron-right</v-icon></v-btn>
          </template>
          <template v-if="step === 3">
            <v-btn @click="step = 2" outlined><v-icon left small>mdi-chevron-left</v-icon>Anterior</v-btn>
            <v-spacer />
            <v-btn @click="step = 4" color="primary">Siguiente <v-icon right small>mdi-chevron-right</v-icon></v-btn>
          </template>
          <template v-if="step === 4">
            <v-btn @click="step = 3" outlined><v-icon left small>mdi-chevron-left</v-icon>Anterior</v-btn>
            <v-spacer />
            <v-btn color="primary" :loading="$store.state.agents.createLoader" :disabled="!form.name || $store.state.agents.createLoader" @click="createAgent">Crear agente</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import GenericViewMixin from '@/mixins/GenericViewMixin'
import {
  agentAvatars,
  agentSuggestedNames,
  agentCollectionsTasks,
  agentBillingTasks,
  agentAllTasks
} from '@/collections/agents'

export default {
  mixins: [GenericViewMixin],
  data () {
    const queryKind = this.$route.query.kind
    const hasQueryKind = !!queryKind
    const isTemplate = hasQueryKind && queryKind !== 'scratch'

    return {
      step: hasQueryKind ? 2 : 1,
      selectedKind: hasQueryKind ? queryKind : null,
      form: {
        kind: isTemplate ? queryKind : 'custom',
        default_autonomy: 'approval_required',
        name: '',
        description: '',
        avatar: null
      },
      availableTasks: hasQueryKind ? this.buildTasks(queryKind) : [],
      avatarOptions: agentAvatars,
      autonomyOptions: [
        {
          value: 'suggest_only',
          label: 'Solo sugerir',
          description: 'El agente presenta sugerencias para que tú decidas qué hacer.',
          icon: 'mdi-eye-outline',
          color: 'blue',
          recommended: false
        },
        {
          value: 'approval_required',
          label: 'Con aprobación',
          description: 'El agente actúa solo después de que tú apruebas cada decisión.',
          icon: 'mdi-check-circle-outline',
          color: 'green',
          recommended: true
        },
        {
          value: 'auto_execute',
          label: 'Automático',
          description: 'El agente ejecuta acciones sin requerir tu intervención.',
          icon: 'mdi-lightning-bolt-outline',
          color: 'orange',
          recommended: false
        }
      ]
    }
  },
  computed: {
    enabledTasksCount () {
      return this.availableTasks.filter(t => t.enabled).length
    }
  },
  methods: {
    selectTemplate (kind) {
      this.selectedKind = kind
      this.form.kind = kind === 'scratch' ? 'custom' : kind
      this.availableTasks = this.buildTasks(kind)
      this.step = 2
    },
    buildTasks (kind) {
      if (kind === 'collections') {
        return agentCollectionsTasks.map(t => ({ ...t, enabled: t.default_enabled }))
      }
      if (kind === 'billing') {
        return agentBillingTasks.map(t => ({ ...t, enabled: t.default_enabled }))
      }
      // scratch: all tasks, none pre-selected
      return agentAllTasks.map(t => ({ ...t, enabled: false }))
    },
    suggestName () {
      const random = agentSuggestedNames[Math.floor(Math.random() * agentSuggestedNames.length)]
      this.form.name = random
    },
    suggestDescription () {
      const kindLabel = this.form.kind === 'collections' ? 'cobranza' : this.form.kind === 'billing' ? 'facturación' : 'automatización'
      const count = this.enabledTasksCount
      this.form.description = `Gestiona automáticamente el proceso de ${kindLabel} con ${count} tarea${count !== 1 ? 's' : ''} activa${count !== 1 ? 's' : ''}.`
    },
    generateKey (name) {
      const slug = name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 48)
      return `${slug}-${Date.now().toString(36)}`
    },
    createAgent () {
      this.$store.dispatch('agents/CREATE', {
        resource: 'agents',
        payload: {
          key: this.generateKey(this.form.name),
          name: this.form.name,
          description: this.form.description,
          kind: this.form.kind,
          status: 'draft',
          default_autonomy: this.form.default_autonomy,
          avatar: this.form.avatar
        }
      })
      .then((response) => {
        const agentId = response.data.id
        const enabledTasks = this.availableTasks.filter(t => t.enabled)
        const modulePromises = enabledTasks.map(task =>
          this.$store.dispatch('agents/CREATE', {
            resource: 'agents/modules',
            payload: {
              agent: agentId,
              module_key: task.task_key,
              is_enabled: true,
              autonomy: task.recommended_autonomy
            },
            commitInstance: false
          })
        )
        return Promise.all(modulePromises)
      })
      .then(() => {
        this.$dialog.message.success('Agente creado exitosamente')
        this.$router.push({ name: 'AgentList' })
      })
      .catch((error) => {
        const msg = this.parseErrors(error.response.data)
        this.$dialog.message.error(msg || 'Ocurrió un error al crear el agente')
      })
    }
  }
}
</script>

<style scoped>
.v-sheet--outlined.v-sheet--active {
  border: 1px solid #3F51B5 !important;
}
.v-application .elevation-0.v-sheet--active,
.v-application .elevation-3:hover.v-sheet--active {
  box-shadow: 0px 0px 0px 3px #CCE1FF !important;
}
.avatar-selected {
  box-shadow: 0 0 0 3px #3F51B5;
  transform: scale(1.1);
}
</style>
